import { apiErrorList } from 'Services/restAdviser'
import { rechargeWithCreditCard as cardRecharge, rechargeWithBoleto as boletoRecharge, rechargeWithPix as pixRecharge } from 'Api/walletStatementApi'
import { getOpportunityDetails, buyOpportunity, discardOpportunity, discardPublicOpportunity } from 'Api/quoteOpportunityApi'
import { parseOpportunityDetail, parseDiscardReason } from 'Parsers/opportunityParser'
import { getPlanStatus } from 'Api/subscriptionApi'
import OpportunityDetails from 'Types/_Store/QuoteOpportunity'
import Vue from 'vue'

const state: OpportunityDetails = {
  requesting: {
    info: false,
    purchase: false,
    discard: false,
    discount: false,
    recharge: false,
    realtime: false
  },

  selected: {
    discardReasonId: null
  },

  companyDiscount: 0,

  connection: null,
  connectionId: null,
  
  opportunity: {
    id: 0,
    companyQuotationId: 0,
    negotiationId: 0,
    token: '',
    category: '',
    serviceType: '',
    isBrandNew: false,
    isPublic: false, 
    isAvailable: null,
    origin: 0,
    price: {
      previous: 0,
      current: 0,
      discount: 0,
      currency: '',
    },
    address: {
      description: '',
      street: '',
      neighborhood: '',
      city: '',
      state: '',
    },
    sender: {
      id: 0,
      name: '',
      email: '',
      avatar: '',
      phoneInfo: '',
      rate: '',
      contactPreferenceDescription: '',
      contactPreference: 0
    },
    request: {
      questionFormList: [],
      questionBuildingList: [],
      details: '',
      deadline: '',
      attachments: 0,
    },
    date: {
      creation: '',
      expiration: '',
      createdSince: '',
      expiresIn: '',
      distanceInHours: '',
    }
  },
}

const mutations = {
  request_start(state, type) {
    state.requesting[type] = true
  },

  request_success(state, type) {
    state.requesting[type] = false
  },

  request_failure(state, type) {
    state.requesting[type] = false
  },

  set_company_discount(state, companyDiscount) {
    state.companyDiscount = companyDiscount
  },

  set_opportunity(state, opportunity) {
    state.opportunity = opportunity
  },
  set_selected_discard_reason(state, id: number) {
    state.selected.discardReasonId = id
  },

  set_connection(state, connection) {
    state.connection = connection
  },

  set_connection_id(state, connectionId: string) {
    state.connectionId = connectionId
  }
}

const actions = {
  async startConnection({ commit }) {
    commit('request_start', 'realtime')

    try {

      const connectionId = await Vue.prototype.$getConnectionId()

      commit('set_connection_id', connectionId)
      commit('request_success', 'realtime')

      return connectionId
    } catch (err) {
      commit('request_failure', 'realtime')
      throw err
    }
  },
  
  async getCompanyDiscount({ commit }) {
    commit('request_start', 'discount')

    try {
      const { data } = await getPlanStatus()

      commit('request_success', 'discount')
      commit('set_company_discount', data.benefit.percentageDiscountQuotations)

      return true
    } catch (err) {
      commit('request_failure', 'discount')

      throw err
    }
  },

  async getDetails({ commit }, quotationId) {
    commit('request_start', 'info')
    try {
      const { data } = await getOpportunityDetails(quotationId)
      const opportunity = parseOpportunityDetail(data)

      commit('set_opportunity', opportunity)

      commit('request_success', 'info')

      return data
    } catch (err) {
      commit('request_failure', 'info')

      throw err
    }
  },

  async buyOpportunity({ commit }, quotationId) {
    commit('request_start', 'purchase')

    try {
      const { data } = await buyOpportunity(quotationId)

      commit('request_success', 'purchase')

      return data
    } catch (err) {
      commit('request_failure', 'purchase')

      throw err
    }
  },

  async rechargeAndBuyWithPix({ commit, dispatch, state }) {
    commit('request_start', 'recharge')

    //Open signalR connection hub
    await dispatch('startConnection');

    const { current } = state.opportunity.price
    const { companyQuotationId } = state.opportunity
    const singleOpportunityPurchase = 4
    const connId = state.connectionId


    try {
      const { data } = await pixRecharge(current, singleOpportunityPurchase, companyQuotationId, connId)
      commit('request_success', 'recharge')

      return data
    } catch (err) {
      const errors = apiErrorList(err)
      commit('request_failure', { errors, type: 'recharge' })

      throw err
    }
  },

  async rechargeAndBuyWithCreditCard({ commit, dispatch, state }, creditCardId) {
    commit('request_start', 'recharge')

    await dispatch('startConnection');

    const { current } = state.opportunity.price
    const { companyQuotationId } = state.opportunity
    const singleOpportunityPurchase = 4
    const connId = state.connectionId


    try {
      const { data } = await cardRecharge(current, creditCardId, false, singleOpportunityPurchase, companyQuotationId, connId)
      commit('request_success', 'recharge')

      return data
    } catch (err) {
      const errors = apiErrorList(err)
      commit('request_failure', { errors, type: 'recharge' })

      throw err
    }
  },

  async discardOpportunity({ commit, dispatch, state }, discardDescription) {
    commit('request_start', 'discard')

    try {

      const { negotiationId } = state.opportunity
      const { discardReasonId } = state.selected

      const data = parseDiscardReason(discardReasonId, negotiationId, discardDescription)
      const result = await discardOpportunity(data)

      dispatch('getOpportunityList')

      commit('request_success', 'discard')

      return result
    } catch (err) {
      commit('request_failure', 'discard')

      throw err
    }
  },
  
  async discardPublicOpportunity({ commit}, opportunityId) {
    commit('request_start', 'discard')
    try {

      const result = await discardPublicOpportunity(opportunityId)

      commit('request_success', 'discard')

      return result
    } catch (err) {
      commit('request_failure', 'discard')

      throw err
    }
  },

  updateDiscardReason({ commit }, discardReasonId: number) {
    commit('set_selected_discard_reason', discardReasonId)
  },
}


export default {
  namespaced: true,

  state,
  mutations,
  actions,
}